import {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  createContext,
  use,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { wrappedIndex } from "@/lib/wrapped-index";
import { useDismissed } from "./dismiss";
import { HoverTip, type HoverTipTriggerProps } from "./HoverTip";
import { CAPTION_LINE } from "./marks";
import { MicroLabel } from "./MicroLabel";
import { type PillTone, pillClasses } from "./StatusPill";

/**
 * A short list of named acts on one thing, behind a trigger in its corner.
 *
 * The design puts every act on a claim or a position that is not its main one
 * here, each with a line saying what it does, rather than in a row of buttons
 * or a dialog of them. One menu for all of them is what lets "Mark as
 * placeholder" read the same wherever it is offered.
 *
 * It is a popover, not a modal: focus is not trapped, and moving it anywhere
 * else closes the menu, as a press outside or Escape does.
 */
const MenuContext = createContext<{ choose: () => void } | null>(null);

const TRIGGER =
  "inline-flex h-[26px] flex-none cursor-pointer items-center gap-1 whitespace-nowrap rounded-sm border border-border bg-card px-2.5 text-[11.5px] font-semibold text-primary hover:bg-muted aria-expanded:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50";

const PANEL =
  "absolute top-[calc(100%+6px)] data-[side=above]:top-auto data-[side=above]:bottom-[calc(100%+6px)] z-40 flex w-[290px] flex-col rounded-menu border border-border bg-card p-[5px] text-left shadow-popover";

/** The account's round mark on the shell's dark bar, ringed white for focus as the bar's controls are. */
const AVATAR_TRIGGER =
  "grid size-[33px] flex-none cursor-pointer place-items-center overflow-hidden rounded-full border border-white/30 bg-brand-mid text-[11px] font-bold tracking-[0.02em] text-white transition-[box-shadow,filter] hover:brightness-[1.08] hover:shadow-[0_0_0_3px_rgba(131,83,253,0.32)] aria-expanded:shadow-[0_0_0_3px_rgba(131,83,253,0.32)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/90 focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar";

const PILL_TRIGGER =
  "cursor-pointer hover:brightness-95 aria-expanded:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50";

/**
 * A trigger with words of its own and a chevron after them: the Options box,
 * or, with a tone, a pill drawn as the state the menu changes.
 */
export interface MenuTriggerLook {
  content: ReactNode;
  pill?: PillTone;
  /** The round mark of someone's account, with no chevron: their initial. */
  avatar?: boolean;
  /** The pill's words as a state's, in capitals; sentence case otherwise. */
  caps?: boolean;
  /** What a pointer resting on the trigger reads. */
  tip?: string;
}

/** Every item the menu draws, an act or a choice among several, in the order shown. */
const ITEM_ROLES = '[role="menuitem"], [role="menuitemradio"]';

function items(panel: HTMLElement | null): HTMLElement[] {
  return panel === null
    ? []
    : [...panel.querySelectorAll<HTMLElement>(ITEM_ROLES)];
}

const STEPS: Partial<Record<string, (at: number, count: number) => number>> = {
  ArrowDown: (at, count) => wrappedIndex(at, 1, count),
  ArrowUp: (at, count) => wrappedIndex(at, -1, count),
  Home: () => 0,
  End: (_, count) => count - 1,
};

/** The edges a panel must stay inside: the nearest box that clips what overflows it, and the window. */
function roomFor(element: HTMLElement): { top: number; bottom: number } {
  let top = 0;
  let bottom = window.innerHeight;
  for (let at = element.parentElement; at !== null; at = at.parentElement) {
    if (getComputedStyle(at).overflowY !== "visible") {
      const edges = at.getBoundingClientRect();
      top = Math.max(top, edges.top);
      bottom = Math.min(bottom, edges.bottom);
      break;
    }
  }
  return { top, bottom };
}

/**
 * Open the panel above its trigger when the space below, in the box that clips it or the
 * window, cannot hold it and the space above can. A row at the foot of a scrolling list would
 * otherwise have its menu cut off by the list. Set on the element before paint, as the side
 * is a fact of the layout and not of what the menu shows.
 */
function useOpensAbove(
  open: boolean,
  panelRef: RefObject<HTMLDivElement | null>,
  triggerRef: RefObject<HTMLButtonElement | null>,
): void {
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const trigger = triggerRef.current;
    if (!open || panel === null || trigger === null) return;
    const height = panel.getBoundingClientRect().height;
    const at = trigger.getBoundingClientRect();
    const room = roomFor(trigger);
    const above =
      at.bottom + GAP + height > room.bottom &&
      at.top - GAP - height >= room.top;
    panel.dataset.side = above ? "above" : "below";
  }, [open, panelRef, triggerRef]);
}

/** The space between the trigger and its panel, as `calc(100%+6px)` draws it. */
const GAP = 6;

export function Menu({
  name,
  trigger,
  align = "end",
  triggerId,
  children,
}: {
  /**
   * What a reader hears for the trigger: "Options for claim 2". With words of
   * its own on the trigger, it starts with those words: "Place in… FIG. 3".
   */
  name: string;
  /** Words in place of "Options", followed by a chevron, on the box or on a pill. */
  trigger?: MenuTriggerLook;
  /**
   * The trigger's edge the panel opens from: its end for a trigger at the
   * right of a row or in a corner, its start for one at the left of a line.
   */
  align?: "start" | "end";
  /** The trigger's id, for a page that sends focus to it from elsewhere. */
  triggerId?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useDismissed(open, panelRef, triggerRef, close);
  useOpensAbove(open, panelRef, triggerRef);

  // On opening, and whenever an item that stays open replaces what the menu
  // shows: the pressed item has then left the document, and focus with it. The
  // replacement is its content's own render, not the menu's, so it is watched.
  useEffect(() => {
    const panel = panelRef.current;
    if (!open || panel === null) return undefined;
    const settle = () => {
      // Without scrolling: a list that scrolls to the item moves the row under the pointer.
      if (!panel.contains(document.activeElement))
        items(panel)[0]?.focus({ preventScroll: true });
    };
    settle();
    const watch = new MutationObserver(settle);
    watch.observe(panel, { childList: true, subtree: true });
    return () => {
      watch.disconnect();
    };
  }, [open]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = STEPS[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const all = items(panelRef.current);
    const at = all.findIndex((item) => item === document.activeElement);
    all[step(at, all.length)]?.focus();
  };

  // Focus goes back to the trigger before the act runs, so an act that opens a
  // dialog is given the trigger as the place to return to when it closes.
  const choose = () => {
    setOpen(false);
    triggerRef.current?.focus({ preventScroll: true });
  };

  return (
    <span className="relative inline-flex">
      <MenuTrigger
        triggerRef={triggerRef}
        triggerId={triggerId}
        name={name}
        open={open}
        panelId={id}
        trigger={trigger}
        onToggle={() => {
          setOpen((was) => !was);
        }}
        onOpen={() => {
          setOpen(true);
        }}
      />
      {open && (
        <div
          ref={panelRef}
          id={id}
          role="menu"
          aria-label={name}
          onKeyDown={onKeyDown}
          className={cn(PANEL, align === "end" ? "right-0" : "left-0")}
        >
          <MenuContext value={{ choose }}>{children}</MenuContext>
        </div>
      )}
    </span>
  );
}

/** The menu's trigger: the word "Options" in a corner, or words of its own on the box or a pill, with its tip. */
function MenuTrigger({
  triggerRef,
  triggerId,
  name,
  open,
  panelId,
  trigger,
  onToggle,
  onOpen,
}: {
  triggerRef: RefObject<HTMLButtonElement | null>;
  triggerId: string | undefined;
  name: string;
  open: boolean;
  panelId: string;
  trigger: MenuTriggerLook | undefined;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const button = (extra: Partial<HoverTipTriggerProps> = {}) => (
    <button
      ref={triggerRef}
      id={triggerId}
      type="button"
      aria-label={name}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? panelId : undefined}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key !== "ArrowDown") return;
        event.preventDefault();
        onOpen();
      }}
      className={triggerClasses(trigger)}
      {...extra}
    >
      {trigger === undefined ? (
        "Options"
      ) : trigger.avatar === true ? (
        trigger.content
      ) : (
        <>
          {trigger.content}
          <ChevronDown
            aria-hidden
            className={trigger.pill === undefined ? "size-3" : undefined}
          />
        </>
      )}
    </button>
  );
  if (trigger?.tip === undefined) return button();
  return (
    <HoverTip tip={trigger.tip} tone="dark" wide={false}>
      {({ triggerProps }) => button(triggerProps)}
    </HoverTip>
  );
}

function triggerClasses(trigger: MenuTriggerLook | undefined): string {
  if (trigger?.avatar === true) return AVATAR_TRIGGER;
  return trigger?.pill === undefined
    ? TRIGGER
    : cn(
        pillClasses(trigger.pill, "chip", trigger.caps !== true),
        PILL_TRIGGER,
      );
}

/** What leads a one-line item: its icon, or for a choice among several a check, or the check's place kept empty. */
function leadOf(checked: boolean | undefined, icon: ReactNode): ReactNode {
  if (checked === undefined) return icon;
  return checked ? (
    <Check aria-hidden />
  ) : (
    <span aria-hidden className="size-[13px] flex-none" />
  );
}

function itemClasses(twoLines: boolean, danger: boolean): string {
  return cn(
    "group/item flex w-full cursor-pointer rounded-sm text-left focus-visible:outline-none",
    twoLines
      ? "flex-col gap-[3px] px-2.5 py-[9px]"
      : "items-center gap-2 px-[9px] py-[7px] text-[12px] leading-[1.3] font-semibold [&_svg]:size-[13px] [&_svg]:flex-none [&_svg]:text-subtle-foreground",
    danger
      ? "hover:bg-destructive-surface focus-visible:bg-destructive-surface"
      : "hover:bg-muted focus-visible:bg-muted",
  );
}

function titleClasses(
  twoLines: boolean,
  danger: boolean,
  brand: boolean,
): string {
  return cn(
    twoLines ? "text-[12.5px] font-bold" : "",
    danger
      ? "text-destructive-strong"
      : brand
        ? "text-primary-soft-foreground"
        : "text-foreground group-hover/item:text-primary group-focus-visible/item:text-primary",
  );
}

/**
 * One act. With a description it is drawn as the design's two lines, a bold
 * title over what it does; without one it is a single line, optionally led by
 * an icon, as the place list draws its destinations.
 *
 * With `checked` it is one choice among several, a radio item marked with a
 * check where it is the current one, and the mark's place kept empty where it
 * is not, so the titles line up. `end` is drawn at the line's far end and read
 * as part of the item's description.
 */
export function MenuItem({
  title,
  description,
  icon,
  checked,
  end,
  danger = false,
  brand = false,
  keepOpen = false,
  onSelect,
}: {
  title: string;
  description?: string;
  icon?: ReactNode;
  /** Whether this choice is the current one, for an item that is one of several. */
  checked?: boolean;
  /** A short mark after the title on a one-line item, such as what kind of choice it is. */
  end?: ReactNode;
  /** An act that takes something away. */
  danger?: boolean;
  /** An act that adds the thing the section is for, as the brand's add controls do. */
  brand?: boolean;
  /** For an item that changes what the menu shows rather than acting. */
  keepOpen?: boolean;
  onSelect: () => void;
}) {
  const menu = use(MenuContext);
  const titleId = useId();
  const descriptionId = useId();
  const endId = useId();
  const described = [
    description === undefined ? null : descriptionId,
    end === undefined ? null : endId,
  ]
    .filter((id) => id !== null)
    .join(" ");
  const lead = leadOf(checked, icon);
  return (
    <button
      type="button"
      role={checked === undefined ? "menuitem" : "menuitemradio"}
      aria-checked={checked}
      tabIndex={-1}
      // Named by the title alone: read whole, the description would be its name too.
      aria-labelledby={titleId}
      aria-describedby={described === "" ? undefined : described}
      onClick={() => {
        if (!keepOpen) menu?.choose();
        onSelect();
      }}
      className={itemClasses(description !== undefined, danger)}
    >
      {lead}
      <span
        id={titleId}
        className={titleClasses(description !== undefined, danger, brand)}
      >
        {title}
      </span>
      {description !== undefined && (
        <span
          id={descriptionId}
          className="text-[11px] leading-[1.45] font-medium text-muted-foreground"
        >
          {description}
        </span>
      )}
      {end !== undefined && (
        <span id={endId} className="ml-auto flex-none">
          {end}
        </span>
      )}
    </button>
  );
}

export function MenuSeparator() {
  return <div role="separator" className="mx-2 my-1 h-px bg-border" />;
}

/** A caption over a run of items, which is not itself one of them. */
function MenuHeading({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <MicroLabel
      as="div"
      size="sm"
      id={id}
      className="px-[9px] pt-[7px] pb-1 font-extrabold"
    >
      {children}
    </MicroLabel>
  );
}

/**
 * A run of items under a caption that names them together, for a menu whose
 * items repeat their words from one run to the next: "As new independent
 * claim" in each form reads as which form it is in. A description is a line
 * under the items saying what choosing one of them also does; a menu holds
 * only items, groups and separators, so it describes the group, not an item.
 */
export function MenuGroup({
  heading,
  description,
  children,
}: {
  heading: string;
  description?: string;
  children: ReactNode;
}) {
  const id = useId();
  const descriptionId = useId();
  return (
    <div
      role="group"
      aria-labelledby={id}
      aria-describedby={description === undefined ? undefined : descriptionId}
    >
      <MenuHeading id={id}>{heading}</MenuHeading>
      {children}
      {description !== undefined && (
        <div
          id={descriptionId}
          className={cn(CAPTION_LINE, "px-[9px] pt-1 pb-[7px] leading-[1.45]")}
        >
          {description}
        </div>
      )}
    </div>
  );
}

/**
 * Whose the menu is, over the acts on it: an account's mark and address, with
 * its acts under a rule. The group is named by the address, so a reader hears
 * whose account the acts belong to.
 */
export function MenuIdentity({
  mark,
  name,
  children,
}: {
  mark: ReactNode;
  name: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <div role="group" aria-labelledby={id}>
      <div className="flex items-center gap-3 px-3 pt-3 pb-3.5">
        <span
          aria-hidden
          className="grid size-11 flex-none place-items-center rounded-full bg-secondary text-[15px] font-bold text-primary"
        >
          {mark}
        </span>
        <span
          id={id}
          className="min-w-0 truncate text-[14px] font-bold tracking-[-0.01em] text-foreground"
        >
          {name}
        </span>
      </div>
      <div className="border-t border-border pt-[5px]">{children}</div>
    </div>
  );
}
